import React, { useRef, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { API } from "aws-amplify";
import { DataAccordion, ResetWorkflowButton, StyledActionButton } from "../components";
import PrescriberSelector from "../components/PrescriberSelector";
import MedicationSelector from "../components/MedicationSelector";
import { MEDICATIONS, PRESCRIBERS } from "../config/PrescriptionDefaults";
import { getApiErrorMessage } from "../../../utils/apiError";
import { IMedicationDetails } from "../types/PrescriptionIngestionTypes";
import { COLORS } from "../styles/theme";
import {
  buildRxChangeResponseRequest,
  DENIAL_REASON_CODES,
  ICodeOption,
  IRxChangeResponseForm,
  IRxChangeResponseResult,
  MAX_NOTE_LENGTH,
  MAX_RELATES_TO_MESSAGE_ID_LENGTH,
  REQUEST_CODES,
  RESPONSE_TYPES,
  RxChangeResponseType,
  validateRxChangeResponseForm,
} from "../utils/rxChangeResponse";

const DEFAULT_MEDICATION_KEY = Object.keys(MEDICATIONS)[0];
const DEFAULT_PRESCRIBER_KEY = "dr_kulkarni";

const CodeSelect: React.FC<{
  id: string;
  label: string;
  value: string;
  options: ICodeOption[];
  onChange: (code: string) => void;
}> = ({ id, label, value, options, onChange }) => (
  <FormControl fullWidth variant="outlined">
    <InputLabel id={`${id}-label`}>{label}</InputLabel>
    <Select
      labelId={`${id}-label`}
      id={id}
      value={value}
      label={label}
      onChange={(e) => onChange(e.target.value as string)}
    >
      {options.map((option) => (
        <MenuItem key={option.code} value={option.code}>
          {option.code} - {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const RxChangeResponseForm: React.FC<{ onReset: () => void }> = ({ onReset }) => {
  const [relatesToMessageId, setRelatesToMessageId] = useState("");
  const [messageRequestCode, setMessageRequestCode] = useState(REQUEST_CODES[0].code);
  const [responseType, setResponseType] = useState<RxChangeResponseType>("APPROVED");
  const [note, setNote] = useState("");
  const [denialReasonCode, setDenialReasonCode] = useState("");
  const [denialReason, setDenialReason] = useState("");
  const [patientId, setPatientId] = useState("");
  const [prescriberKey, setPrescriberKey] = useState<string>(DEFAULT_PRESCRIBER_KEY);
  const [medicationKey, setMedicationKey] = useState(DEFAULT_MEDICATION_KEY);
  const [medication, setMedication] = useState<IMedicationDetails>(MEDICATIONS[DEFAULT_MEDICATION_KEY]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IRxChangeResponseResult | null>(null);

  const denied = responseType === "DENIED";

  const handleSubmit = async () => {
    const form: IRxChangeResponseForm = {
      relatesToMessageId,
      messageRequestCode,
      responseType,
      note,
      denialReasonCode,
      denialReason,
      patientId,
      prescriber: PRESCRIBERS[prescriberKey],
      medication,
    };
    setResult(null);
    const validationError = validateRxChangeResponseForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = (await API.post("ApaqPortalAPI", "/prescription-messaging/ingest-rx-change-response", {
        body: buildRxChangeResponseRequest(form),
      })) as IRxChangeResponseResult;
      setResult(response);
    } catch (e) {
      setError(getApiErrorMessage(e, "Request failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid container direction="column" spacing={3} style={{ marginTop: 8 }}>
      <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
        <Paper style={{ padding: 24 }}>
          <Typography variant="h6" gutterBottom>
            Ingest RxChangeResponse
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Sends the prescriber's answer to a pharmacy RxChangeRequest through PMGS to UPMS.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                id="rcr-relates-to-message-id"
                label="RelatesToMessageID (Required)"
                variant="outlined"
                fullWidth
                value={relatesToMessageId}
                onChange={(e) => setRelatesToMessageId(e.target.value)}
                inputProps={{ maxLength: MAX_RELATES_TO_MESSAGE_ID_LENGTH }}
                helperText="MessageID of the RxChangeRequest being answered"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CodeSelect
                id="rcr-request-code"
                label="Request Code"
                value={messageRequestCode}
                options={REQUEST_CODES}
                onChange={setMessageRequestCode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="rcr-patient-id"
                label="Person ID or Profile ID (Required)"
                variant="outlined"
                fullWidth
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                row
                aria-label="Response type"
                value={responseType}
                onChange={(e) => setResponseType(e.target.value as RxChangeResponseType)}
              >
                {RESPONSE_TYPES.map((type) => (
                  <FormControlLabel key={type.value} value={type.value} control={<Radio />} label={type.label} />
                ))}
              </RadioGroup>
            </Grid>

            {denied ? (
              <>
                <Grid item xs={12} md={6}>
                  <CodeSelect
                    id="rcr-denial-reason-code"
                    label="Denial Reason (Required)"
                    value={denialReasonCode}
                    options={DENIAL_REASON_CODES}
                    onChange={setDenialReasonCode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="rcr-denial-reason"
                    label="Denial Details"
                    variant="outlined"
                    fullWidth
                    value={denialReason}
                    onChange={(e) => setDenialReason(e.target.value)}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <TextField
                  id="rcr-note"
                  label="Note"
                  variant="outlined"
                  fullWidth
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                  helperText={`${note.length}/${MAX_NOTE_LENGTH}`}
                />
              </Grid>
            )}

            <PrescriberSelector
              selectedPrescriber={prescriberKey}
              prescriberDetails={PRESCRIBERS[prescriberKey]}
              onPrescriberChange={setPrescriberKey}
            />

            {!denied && (
              <MedicationSelector
                selectedMedication={medicationKey}
                medicationDetails={medication}
                onMedicationChange={(key) => {
                  setMedicationKey(key);
                  setMedication(MEDICATIONS[key]);
                }}
                onMedicationFieldChange={(field, value) => setMedication((prev) => ({ ...prev, [field]: value }))}
                editableRefills={responseType === "APPROVED_WITH_CHANGES"}
              />
            )}

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <StyledActionButton
                label="Send RxChangeResponse"
                loadingLabel="Sending..."
                onClick={handleSubmit}
                loading={submitting}
                color={COLORS.orange}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {result && (
        <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
          <Paper style={{ padding: 24 }}>
            <Alert severity={result.success ? "success" : "error"} style={{ marginBottom: 16 }}>
              {result.success
                ? "PMGS accepted the RxChangeResponse. It reaches UPMS asynchronously."
                : `PMGS rejected the RxChangeResponse: ${result.statusCode || ""} ${result.statusDescription || ""}`}
            </Alert>
            <Typography variant="body2">
              Sent MessageID: <strong>{result.sentMessageId}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 16 }}>
              Search the PMGS logs with this ID to trace the message.
            </Typography>
            <DataAccordion title="PMGS Response" data={result} />
          </Paper>
          <ResetWorkflowButton label="Start New RxChangeResponse" onReset={onReset} />
        </Grid>
      )}
    </Grid>
  );
};

const RxChangeResponseSection: React.FC = () => {
  const [formKey, setFormKey] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setFormKey((key) => key + 1);
    topRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={topRef}>
      <RxChangeResponseForm key={formKey} onReset={reset} />
    </div>
  );
};

export default RxChangeResponseSection;
